import {
  IPlayerCheckUpdateResponse,
  IPlayerCommandResultResponse,
  IPlayerErrorResponse,
  IPlayerHeartbeatResponse,
  IPlayerPairPollResponse,
  IPlayerPairStartResponse,
  IPlayerPlaylistResponse,
  IPlayerRegisterResponse,
} from "./types";
import { BrowserHeartbeatMeta } from "./deviceMeta";

/**
 * Player API client. Implements the contract from docs/player-api.md
 * exactly — no extra headers, no rewrites. The backend resolves the
 * tenant from the bearer token; the player never sends company_id.
 */

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

/**
 * Web Player build version. Bump in lockstep with the deployment that
 * needs to be tracked by the server's app-update bookkeeping. The
 * browser refreshes the underlying bundle on its own (cache headers /
 * SW); this value just lets admins see "code 7 deployed" in the
 * monitoring dashboard.
 */
export const WEB_PLAYER_VERSION_CODE = 1;

export type PlayerApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; data: IPlayerErrorResponse | null };

const headersFor = (token: string | null): HeadersInit => {
  const h: Record<string, string> = { Accept: "application/json" };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

async function request<T>(
  method: "GET" | "POST",
  path: string,
  token: string | null,
  body?: object,
): Promise<PlayerApiResult<T>> {
  try {
    const res = await fetch(`${API_URL}${path}`, {
      method,
      headers: {
        ...headersFor(token),
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
      // Player API uses bearer auth only — cookies would just pollute.
      credentials: "omit",
    });
    let payload: any = null;
    try { payload = await res.json(); } catch { /* empty body */ }
    if (res.ok) return { ok: true, status: res.status, data: payload as T };
    return { ok: false, status: res.status, data: payload };
  } catch (e) {
    // Network error — propagate as a not-ok result so the caller can
    // fall back to the cached playlist without crashing.
    return { ok: false, status: 0, data: null };
  }
}

export const PlayerApi = {
  register: (deviceCode: string, appVersion: string) =>
    request<IPlayerRegisterResponse>("POST", "/device/register", null, {
      device_code: deviceCode,
      app_version: appVersion,
    }),

  /**
   * TV-initiated pairing — step 1. The browser asks the server for a
   * pairing session. No auth yet. Returns the code to DISPLAY plus the
   * private session token used to poll. `platform` is a free-text hint
   * surfaced in the admin pairing meta; the credential is still minted
   * by the admin claim + poll, never here.
   */
  startPairing: (appVersion: string, platform: string, fingerprint?: string) =>
    request<IPlayerPairStartResponse>("POST", "/device/pair/start", null, {
      app_version: appVersion,
      platform,
      ...(fingerprint ? { device_fingerprint: fingerprint } : {}),
    }),

  /**
   * TV-initiated pairing — step 2. Poll with the private session token.
   * While the admin hasn't claimed the code the status is "pending";
   * once claimed the NEXT poll returns status "activated" with the
   * device credential exactly once.
   */
  pollPairing: (sessionToken: string) =>
    request<IPlayerPairPollResponse>("POST", "/device/pair/poll", null, {
      session_token: sessionToken,
    }),

  playlist: (deviceCode: string, token: string) =>
    request<IPlayerPlaylistResponse>(
      "GET",
      `/device/playlist/${encodeURIComponent(deviceCode)}`,
      token,
    ),

  checkUpdate: (deviceCode: string, token: string) =>
    request<IPlayerCheckUpdateResponse>(
      "GET",
      `/device/check-update/${encodeURIComponent(deviceCode)}`,
      token,
    ),

  heartbeat: (
    deviceCode: string,
    token: string,
    playlistVersion: number,
    appVersion: string,
    meta?: BrowserHeartbeatMeta,
  ) =>
    request<IPlayerHeartbeatResponse>("POST", "/device/heartbeat", token, {
      device_code: deviceCode,
      playlist_version: playlistVersion,
      app_version: appVersion,
      player_type: "web",
      // Numeric build version of the Web Player. Bumped manually when
      // the bundle changes in a way we want to track on the server —
      // mostly for the admin "current vs latest" badge on the
      // monitoring page.
      app_version_code: WEB_PLAYER_VERSION_CODE,
      update_channel: "stable",
      // Runtime metadata snapshot. All optional server-side; the server
      // only writes it when a value changes. Resolution/orientation are
      // recomputed each heartbeat so a display/resolution change is
      // reconciled on the next tick without any extra polling.
      ...(meta ?? {}),
    }),

  commandResult: (
    token: string,
    commandId: number,
    status: "completed" | "failed",
    message?: string,
  ) =>
    request<IPlayerCommandResultResponse>("POST", "/device/command-result", token, {
      command_id: commandId,
      status,
      message: message ?? null,
    }),

  /**
   * Fire-and-forget event report. Errors are swallowed because event
   * logging must NEVER affect playback. If the server is unreachable
   * the event is silently dropped.
   */
  event: async (
    token: string,
    eventType: string,
    message?: string,
    metadata?: Record<string, unknown>,
  ): Promise<void> => {
    try {
      await fetch(`${API_URL}/device/event`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "omit",
        body: JSON.stringify({
          event_type: eventType,
          message,
          metadata,
          occurred_at: new Date().toISOString(),
        }),
      });
    } catch { /* never bubble up */ }
  },

  /**
   * Multipart file upload to /device/screenshot. We bypass the shared
   * `request` helper because that's JSON-only.
   */
  uploadScreenshot: async (token: string, blob: Blob, filename = "screenshot.jpg"): Promise<boolean> => {
    try {
      const form = new FormData();
      form.append("image", blob, filename);
      const res = await fetch(`${API_URL}/device/screenshot`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        credentials: "omit",
        body: form,
      });
      return res.ok;
    } catch {
      return false;
    }
  },
};
